// Identidad del usuario. ADR-012: Entra ID (SSO corporativo).
//
// ⚠ **HU-45 es de Sprint 3.** El login real no entra en el Sprint 1, y esto lo
// refleja en vez de simularlo: la aplicacion trabaja con el actor simulado que el
// backend acepta SOLO en desarrollo, y lo dice en pantalla.
//
// Desde T-39 (MG-ILE-471) el actor ya no es una constante: la pantalla de entrada
// simulada deja ELEGIR rol y nombre, porque la bandeja y el expediente se validan
// con roles distintos y recompilar para cambiar de rol no es una opcion de QA.
//
// La eleccion vive en `sessionStorage`: sobrevive a recargar —que es lo que hace un
// registrador que pierde el hilo— y muere al cerrar la pestana, que es lo mas
// parecido a "cerrar sesion" que una identidad simulada puede ofrecer sin mentir.
//
// Se aisla aqui —y no con un `usuario` en duro dentro de `App.jsx`— porque cuando
// llegue Entra ID el cambio es sustituir estas funciones por la adquisicion del
// token con MSAL: `usuarioActual()` leera la cuenta, `iniciarSesionSimulada` y
// `cerrarSesionSimulada` se volveran el redirect de MSAL. MISMAS FIRMAS exportadas
// a proposito: ningun componente que las llame cambia.
//
// El backend tiene una DOBLE guarda que impide que el actor simulado sea alcanzable
// en produccion (`validar_para_el_entorno`), asi que esto no puede convertirse en un
// agujero por descuido: si alguien despliega con esta pantalla tal cual, la API
// rechaza las peticiones en vez de aceptarlas sin identidad.

import { ROLES } from '../data/navegacion';
import { olvidarBorradorDeLaPestana } from './borradorLocal';
import { modoDeIdentidad, salirDeEntra } from './identidad';
import type { SesionDelServidor } from '../api/sesion';

const CLAVE = 'fv_sesion_simulada';
// HU-32a (32a-6): con Entra, la persona que devolvio `GET /sesion`, para quien la lee fuera de
// la raiz (`usuarioActual()` en `CierreDelCaso`). No es la sesion: la sesion es el token.
const CLAVE_DE_LA_SESION = 'fv_sesion';

// SANEAMIENTO DE LO QUE SE PERSISTE (jssecurity:S8475, T-45).
//
// El rol y el nombre los TECLEA/ELIGE el usuario y acaban en `sessionStorage`, de
// donde los relee toda la aplicacion: el rol decide que modulos se pintan y el
// `pais` del usuario llega a componer la URL de `GET /casos/series/{pais}`. Un dato
// que entra crudo por un lado y sale por el otro es un dato contaminado, aunque el
// unico que pueda contaminarlo sea el propio usuario de la pestana: la consola del
// navegador —o un `iniciarSesionSimulada` llamado desde otro sitio— basta para
// meter ahi lo que sea. Se sanea AL ESCRIBIR y se vuelve a comprobar AL LEER,
// porque el almacenamiento se puede editar a mano entre las dos cosas.

/** Los roles que la aplicacion define. Sale de `ROLES`, asi que no pueden divergir. */
export type RolConocido = keyof typeof ROLES;

/**
 * La persona de esta pestana. Se llama `UsuarioSimulado` por historia (T-39) y conserva el
 * nombre para no tocar a quien lo importa; con Entra la arma `usuarioDesdeLaSesion`.
 *
 * `roles` (HU-32b, multi-rol): los codigos de la tabla `rol`. Con la entrada simulada es solo el
 * rol elegido; con Entra, los de `GET /sesion`. `rol` es el primero que la aplicacion conoce: lo
 * siguen leyendo pantallas que deciden por un solo rol (el especialista ve sus invitaciones).
 * Para preguntar por un permiso de rol, `tieneRol`, que mira todos.
 */
export type UsuarioSimulado = {
  nombre: string;
  rol: RolConocido;
  roles: string[];
  pais: string;
  simulado: boolean;
  inicial: string;
};

/** El mismo tipo, con el nombre que tiene desde HU-32a. */
export type UsuarioDeLaSesion = UsuarioSimulado;

/** `true` si la persona tiene ese rol entre los suyos (multi-rol: permisos = union). */
export const tieneRol = (usuario: Pick<UsuarioSimulado, 'roles'> | null | undefined, rol: string): boolean =>
  Boolean(usuario?.roles?.includes(rol));

/**
 * El rol si es uno de los que la aplicacion define, o `null`.
 *
 * `Object.hasOwn` y no `ROLES[rol]`: con la indexacion directa, `'constructor'` o
 * `'toString'` devuelven algo desde el prototipo y pasarian por roles validos.
 */
const rolConocido = (rol: unknown): RolConocido | null =>
  (typeof rol === 'string' && Object.hasOwn(ROLES, rol) ? (rol as RolConocido) : null);

// Nombres propios: el MISMO alfabeto que `soloLetras` de `entradas.js` —letras con
// tildes y ñ, espacio, punto, guion y apostrofo ("Dr. José Pérez-O'Brien")— porque
// es el filtro que ya se aplica al notificante y no tiene sentido que la firma del
// borrador admita mas. Fuera queda todo lo demas: cifras, `<`, `"`, llaves.
const FUERA_DEL_NOMBRE = /[^\p{L}\s.'’-]/gu;
const ESPACIOS_SEGUIDOS = /\s+/g;
// El campo de la pantalla ya lo limita a 120; aqui se recorta igual, porque quien
// llame a esta funcion no tiene por que haber pasado por ese campo.
const LARGO_MAXIMO_DEL_NOMBRE = 120;

const nombreSaneado = (nombre: unknown): string =>
  String(nombre ?? '')
    .replaceAll(FUERA_DEL_NOMBRE, '')
    .replaceAll(ESPACIOS_SEGUIDOS, ' ')
    .trim()
    .slice(0, LARGO_MAXIMO_DEL_NOMBRE);

// El pais viaja a la URL de la serie correlativa: solo un codigo ISO de dos letras.
const PAIS_VALIDO = /^[A-Z]{2}$/;
const PAIS_POR_DEFECTO = 'PE';
const paisSaneado = (pais: unknown): string =>
  (typeof pais === 'string' && PAIS_VALIDO.test(pais) ? pais : PAIS_POR_DEFECTO);

// `AppShell` pinta el avatar con `usuario.inicial`; se calcula del nombre para no
// pedir un dato mas en la pantalla de entrada.
const inicialDe = (nombre: unknown): string => (String(nombre || '').trim().charAt(0) || '?').toUpperCase();

/**
 * Registra la eleccion de rol y nombre de la pantalla de entrada simulada.
 *
 * Devuelve el usuario ya armado ademas de persistirlo: si `sessionStorage` esta
 * deshabilitado la sesion vive igual en la memoria de `App`, solo que no
 * sobrevive a recargar — degradacion, no bloqueo.
 *
 * Devuelve `null` —y NO persiste nada— si el rol no es uno de los conocidos. Es la
 * misma senal que da `usuarioActual()` cuando no hay eleccion: la pantalla de
 * entrada sigue en pantalla, que es lo que corresponde cuando la eleccion no vale.
 */
export function iniciarSesionSimulada({ rol, nombre }: { rol?: unknown; nombre?: unknown } = {}): UsuarioSimulado | null {
  const rolElegido = rolConocido(rol);
  if (!rolElegido) return null;
  const nombreLimpio = nombreSaneado(nombre);
  const nombreFinal = nombreLimpio || ROLES[rolElegido].label;
  // La `inicial` se calcula ANTES y el objeto se construye completo, en vez de
  // agregarsela despues: un objeto que nace incompleto y se rellena a continuacion es
  // un objeto que en algun punto del programa no cumple su propio tipo.
  const usuario: UsuarioSimulado = {
    // Sin nombre tecleado se usa la etiqueta del rol («Comité de FV»): dice en la cabecera
    // QUIEN se esta simulando, que es lo que QA necesita leer. Sin el «de pruebas» de antes,
    // que salia tambien en las firmas y en las tablas (decision del operador, 5-oct-2026).
    nombre: nombreFinal,
    rol: rolElegido,
    roles: [rolElegido],
    pais: PAIS_POR_DEFECTO,
    simulado: true,
    inicial: inicialDe(nombreFinal),
  };
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify(usuario));
  } catch {
    // Almacenamiento lleno o bloqueado: la sesion sigue en memoria, solo no
    // sobrevive a recargar. No merece romper la entrada.
  }
  return usuario;
}

/** Cierra la sesion simulada ("cambiar de rol"). El proximo arranque pide elegir. */
export function cerrarSesionSimulada() {
  try {
    sessionStorage.removeItem(CLAVE);
    // Y el borrador que esta pestana tenia abierto (CP-06-05): si no, quien entre
    // despues en la misma pestana aterrizaria en un caso ajeno.
    olvidarBorradorDeLaPestana();
  } catch {
    /* nada que hacer: sin almacenamiento no habia nada persistido */
  }
}

/**
 * El usuario elegido en esta pestana, o `null` si todavia no eligio.
 *
 * `null` es la senal para que `App` muestre la pantalla de entrada. Un rol que ya
 * no exista en `ROLES` —renombrado entre despliegues— tambien devuelve `null`:
 * mejor volver a elegir que arrancar con un menu vacio.
 *
 * SE VUELVE A SANEAR AQUI, no solo al escribir: entre una cosa y otra el
 * almacenamiento es editable a mano desde la consola, y lo que sale de aqui
 * alimenta el menu, la firma del borrador y la URL de la serie correlativa.
 */
export function usuarioActual(): UsuarioSimulado | null {
  if (modoDeIdentidad() === 'entra') return usuarioDeLaSesionGuardada();
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    if (!crudo) return null;
    const usuario = JSON.parse(crudo);
    const rol = rolConocido(usuario?.rol);
    if (!rol) return null;
    // Sin nombre tecleado se guardo la etiqueta del rol, que es una constante de la aplicacion y
    // no un dato del usuario: se devuelve tal cual. Sanearla la mutilaba («VP / Gerencia» ->
    // «VP Gerencia»), dejaba de reconocerse como «sin nombre» y la cabecera inventaba una persona
    // («VP Gerencia · Aprobador (VP / Gerencia)», `sesionDeLaEntradaSimulada`).
    const etiqueta = ROLES[rol].label;
    const nombre = usuario.nombre === etiqueta ? etiqueta : (nombreSaneado(usuario.nombre) || etiqueta);
    return {
      ...usuario,
      rol,
      roles: [rol],
      nombre,
      pais: paisSaneado(usuario.pais),
      inicial: inicialDe(nombre),
      // Se reafirma al leer: un valor guardado a mano no puede hacerse pasar por
      // una identidad real.
      simulado: true,
    };
  } catch {
    return null;
  }
}

/** true mientras la identidad no pase por Entra ID (sin su configuracion). */
export function laIdentidadEsSimulada() {
  return modoDeIdentidad() === 'simulado';
}

// ---------------------------------------------------------------------------------
// HU-32a (32a-6): la persona que devuelve `GET /sesion`
// ---------------------------------------------------------------------------------

/**
 * La persona de la sesion del servidor, en la forma que leen las pantallas. `null` sin ningun rol
 * que la aplicacion conozca: es la persona «Sin acceso» y no llega a ninguna pantalla.
 *
 * Con la entrada simulada el rol elegido manda (`simulado`): el actor simulado del backend tiene
 * todos los roles y la eleccion es la lente con la que se prueba cada pantalla.
 */
export function usuarioDesdeLaSesion(sesion: SesionDelServidor, simulado: UsuarioSimulado | null = null): UsuarioSimulado | null {
  if (simulado) return simulado;
  const roles = sesion.roles.map((r) => r.rol);
  const rol = roles.map(rolConocido).find(Boolean) ?? null;
  if (!rol) return null;
  const nombre = nombreSaneado(sesion.usuario) || sesion.correo;
  const paisDelRol = sesion.roles.find((r) => r.paises.length)?.paises[0];
  return { nombre, rol, roles, pais: paisSaneado(paisDelRol), simulado: false, inicial: inicialDe(nombre) };
}

/**
 * La sesion del actor simulado vista desde el rol elegido (decision del operador, 5-oct-2026).
 *
 * El actor simulado del backend tiene los ocho roles con todos los paises: la cabecera decia
 * «Desarrollo local · Registrador +7 roles» y «Mis roles y países» listaba ocho roles que no
 * informaban de nada. Con la entrada simulada la cabecera y ese panel muestran solo el rol
 * elegido y, si se tecleo, el nombre. Los modulos y los permisos no cambian: los decide el
 * servidor. Con Entra ID no se llama: la sesion real se pinta tal cual.
 */
export function sesionDeLaEntradaSimulada(sesion: SesionDelServidor, usuario: UsuarioSimulado): SesionDelServidor {
  const etiqueta = ROLES[usuario.rol].label;
  const elegido = sesion.roles.find((r) => r.rol === usuario.rol);
  const textoDelRol = elegido?.texto_del_rol ?? etiqueta;
  // Sin nombre tecleado, `usuario.nombre` ES la etiqueta del rol: no se repite.
  const conNombre = usuario.nombre !== etiqueta;
  return {
    ...sesion,
    usuario: usuario.nombre,
    texto_de_la_cabecera: conNombre ? `${usuario.nombre} · ${textoDelRol}` : textoDelRol,
    roles: elegido ? [elegido] : [],
  };
}

/** Deja a mano la persona de la sesion para `usuarioActual()`, que se lee fuera de la raiz. */
export function recordarUsuarioDeLaSesion(usuario: UsuarioSimulado | null): void {
  try {
    if (usuario && !usuario.simulado) sessionStorage.setItem(CLAVE_DE_LA_SESION, JSON.stringify(usuario));
  } catch {
    /* sin almacenamiento, `usuarioActual()` no la vera fuera de la raiz: no rompe nada */
  }
}

function usuarioDeLaSesionGuardada(): UsuarioSimulado | null {
  try {
    const crudo = sessionStorage.getItem(CLAVE_DE_LA_SESION);
    if (!crudo) return null;
    const guardado = JSON.parse(crudo) as Partial<UsuarioSimulado>;
    const roles = Array.isArray(guardado.roles) ? guardado.roles.filter((r): r is string => typeof r === 'string') : [];
    const rol = roles.map(rolConocido).find(Boolean) ?? null;
    if (!rol) return null;
    const nombre = nombreSaneado(guardado.nombre);
    return { nombre, rol, roles, pais: paisSaneado(guardado.pais), simulado: false, inicial: inicialDe(nombre) };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------------
// HU-32a-C8 (32a-7, cierra el work item 338): cerrar sesion no deja casos en el equipo
// ---------------------------------------------------------------------------------

const PREFIJO_DEL_BORRADOR = 'fv_borrador_';
const PREFIJO_DE_LA_APLICACION = 'fv_';

/** Las claves de un almacen que cumplen una condicion (se leen antes de borrar: borrar mueve los indices). */
const clavesDe = (almacen: Storage, condicion: (clave: string) => boolean): string[] =>
  Array.from({ length: almacen.length }, (_, i) => almacen.key(i)).filter((c): c is string => Boolean(c && condicion(c)));

/**
 * Borra del equipo lo que la aplicacion dejo: en `localStorage`, toda clave `fv_borrador_*` (los
 * espejos del borrador, con el texto del caso, y el puntero del borrador en curso); en
 * `sessionStorage`, todas las de la aplicacion (`fv_*`: la sesion, la vista, los filtros, el
 * puntero de la pestana). La cache de MSAL no se toca aqui: la limpia `logoutRedirect`, que la
 * necesita para cerrar la sesion de la cuenta correcta.
 */
export function borrarDatosLocales(): void {
  try {
    clavesDe(localStorage, (c) => c.startsWith(PREFIJO_DEL_BORRADOR)).forEach((c) => localStorage.removeItem(c));
  } catch {
    /* sin almacenamiento no habia nada que borrar */
  }
  try {
    clavesDe(sessionStorage, (c) => c.startsWith(PREFIJO_DE_LA_APLICACION)).forEach((c) => sessionStorage.removeItem(c));
  } catch {
    /* idem */
  }
}

/**
 * «Cerrar sesion» (HU-32a-C8). Primero se borra lo local y despues, con Entra, `logoutRedirect`:
 * si la redireccion falla, el equipo ya quedo limpio.
 */
export async function cerrarSesion(): Promise<void> {
  borrarDatosLocales();
  if (modoDeIdentidad() === 'entra') await salirDeEntra();
}
